/**
 * Identity keys + TOFU pinning for the E2EE tunnel (ADR-014).
 *
 * A long-term Ed25519 keypair per device signs the per-call ephemeral X25519
 * key, so the relay server can no longer swap keys unnoticed. Peer identity
 * keys are pinned on first contact (TOFU); a later change is surfaced loudly
 * instead of being silently accepted.
 *
 * Pure logic, no DOM: `storage` is injected so this runs under node --test.
 * Web passes localStorage; the module falls back to it in the browser.
 */
import nacl from 'tweetnacl';

export const IDENTITY_STORAGE_KEY = 'voxiox_identity_v1';
export const TOFU_STORAGE_KEY = 'voxiox_tofu_v1';

/**
 * Both stores are scoped to the signed-in account.
 *
 * They were not, and on a shared browser that is a real failure: log out,
 * log in as somebody else, and you inherit the previous account's device
 * identity and — worse — every key it had pinned. The second account then
 * believes it has "already met" people it has never contacted, which is
 * exactly the belief TOFU exists to hold honestly.
 *
 * Scoping the identity too, not just the pins: one device key shared by two
 * accounts links them to anyone who sees both.
 *
 * Legacy blobs written before scoping are adopted, once, by the first account
 * to ask for them — otherwise today's users would silently become new devices
 * to all their contacts.
 */
function scopedKey(base, accountId) {
  return accountId == null ? base : `${base}:${accountId}`;
}

function adoptLegacy(storage, base, accountId) {
  if (accountId == null) return;
  const scoped = scopedKey(base, accountId);
  try {
    if (storage.getItem(scoped) != null) return;
    const legacy = storage.getItem(base);
    if (legacy == null) return;
    storage.setItem(scoped, legacy);
    storage.removeItem(base);
  } catch (e) { /* a storage that refuses is a storage without the legacy blob */ }
}

// Domain tags — a signature made here must never validate in another context.
export const SIG_CONTEXT = 'VoxioxTunnelAuthV1';
export const COMMIT_CONTEXT = 'VoxioxCommitV1';

const enc = new TextEncoder();

// ─── Byte helpers (base64 without Buffer, works in browser and node) ────────
export function b64enc(u8) {
  let s = '';
  for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
  return btoa(s);
}
export function b64dec(s) {
  const bin = atob(s);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return u8;
}
function concat(...arrays) {
  const total = arrays.reduce((n, a) => n + a.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrays) { out.set(a, off); off += a.length; }
  return out;
}
function uint32be(n) {
  return new Uint8Array([(n >>> 24) & 0xff, (n >>> 16) & 0xff, (n >>> 8) & 0xff, n & 0xff]);
}

function defaultStorage() {
  if (typeof localStorage !== 'undefined') return localStorage;
  const mem = new Map();
  return {
    getItem: (k) => (mem.has(k) ? mem.get(k) : null),
    setItem: (k, v) => mem.set(k, String(v)),
    removeItem: (k) => mem.delete(k),
  };
}

// ─── Signature payload — what both sides agree is being signed ──────────────
// Binding the room number stops a signature from being replayed into another
// call; the ephemeral key binds it to this specific handshake.
export function signaturePayload(room, ephemeralPub) {
  return concat(enc.encode(SIG_CONTEXT), uint32be(Number(room) || 0), ephemeralPub);
}

// ─── Commitment (astrocall-46p): publish a hash before revealing the key ────
// Without it, whoever moves second can grind ~2^32 ephemeral keys to force a
// chosen 4-emoji SAS. With it, the key is locked in before the peer's is seen.
export function commitTo(ephemeralPub) {
  return nacl.hash(concat(enc.encode(COMMIT_CONTEXT), ephemeralPub)).slice(0, 32);
}
export function commitMatches(commit, ephemeralPub) {
  if (!commit || commit.length !== 32) return false;
  const expected = commitTo(ephemeralPub);
  // nacl.verify is constant-time
  return nacl.verify(new Uint8Array(commit), expected);
}

// ─── Our own device identity ────────────────────────────────────────────────
export class Identity {
  constructor(storage = defaultStorage(), accountId = null) {
    this.storage = storage;
    this.accountId = accountId;
    this._kp = null;
    this._dh = null;
  }

  /**
   * Point at an account's keys. Called once the signed-in user is known, and
   * again on logout with null.
   */
  setAccount(accountId) {
    const next = accountId == null ? null : String(accountId);
    if (next === this.accountId) return;
    this.accountId = next;
    this._kp = null;                       // the cached key belongs to whoever was here before
    this._dh = null;
    adoptLegacy(this.storage, IDENTITY_STORAGE_KEY, next);
  }

  get _key() { return scopedKey(IDENTITY_STORAGE_KEY, this.accountId); }

  /** Load the device identity, generating and persisting one on first use. */
  keyPair() {
    if (this._kp) return this._kp;
    const stored = this.storage.getItem(this._key);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const secret = b64dec(parsed.secretKey);
        if (secret.length === nacl.sign.secretKeyLength) {
          this._kp = nacl.sign.keyPair.fromSecretKey(secret);
          this._dh = parsed.dhSecretKey
            ? nacl.box.keyPair.fromSecretKey(b64dec(parsed.dhSecretKey))
            : null;
          // A device that predates X3DH has no DH key. Add one rather than
          // regenerate the identity: changing the signing key would make this
          // device new to every contact, and the warning that produces is
          // supposed to mean something.
          if (!this._dh) { this._dh = nacl.box.keyPair(); this._persist(parsed.createdAt); }
          return this._kp;
        }
      } catch (e) { /* corrupted — regenerate below */ }
    }
    this._kp = nacl.sign.keyPair();
    this._dh = nacl.box.keyPair();
    this._persist(new Date().toISOString());
    return this._kp;
  }

  _persist(createdAt) {
    this.storage.setItem(this._key, JSON.stringify({
      secretKey: b64enc(this._kp.secretKey),
      // ADR-016: X3DH needs a Curve25519 agreement key, and neither tweetnacl
      // nor CryptoKit converts an Ed25519 key into one. Two keypairs side by
      // side, bound together by the device id and the bundle signature.
      dhSecretKey: b64enc(this._dh.secretKey),
      createdAt: createdAt || new Date().toISOString(),
    }));
  }

  /** The X25519 pair this device agrees with. Same lifetime as the signing one. */
  dhKeyPair() {
    if (!this._dh) this.keyPair();
    return this._dh;
  }
  dhPublicKeyB64() { return b64enc(this.dhKeyPair().publicKey); }

  publicKey() { return this.keyPair().publicKey; }
  publicKeyB64() { return b64enc(this.publicKey()); }

  /** Sign our ephemeral X25519 key for this room. */
  sign(room, ephemeralPub) {
    return nacl.sign.detached(signaturePayload(room, ephemeralPub), this.keyPair().secretKey);
  }

  /** Destroy the device identity — every peer will see us as a new device. */
  reset() {
    this._kp = null;
    this._dh = null;
    this.storage.removeItem(this._key);
  }
}

/** Verify a peer's signature over their ephemeral key. */
export function verifySignature(room, ephemeralPub, identityPub, sig) {
  if (!identityPub || !sig) return false;
  if (identityPub.length !== nacl.sign.publicKeyLength) return false;
  if (sig.length !== nacl.sign.signatureLength) return false;
  try {
    return nacl.sign.detached.verify(signaturePayload(room, ephemeralPub), sig, identityPub);
  } catch (e) {
    return false;
  }
}

// ─── TOFU store ─────────────────────────────────────────────────────────────
// Shape: { [peerId]: [{ key: b64, firstSeen: iso, verified: bool, label?: string }] }
// A user legitimately has several keys (web + iOS + Android), so a peer maps to
// a SET of keys: an unknown key from a known peer is "new device", not "attack".
export class TofuStore {
  constructor(storage = defaultStorage(), accountId = null) {
    this.storage = storage;
    this.accountId = accountId;
  }

  setAccount(accountId) {
    const next = accountId == null ? null : String(accountId);
    if (next === this.accountId) return;
    this.accountId = next;
    adoptLegacy(this.storage, TOFU_STORAGE_KEY, next);
  }

  get _key() { return scopedKey(TOFU_STORAGE_KEY, this.accountId); }

  _read() {
    try {
      const raw = this.storage.getItem(this._key);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  _write(data) {
    this.storage.setItem(this._key, JSON.stringify(data));
  }

  /** All pinned keys for a peer. */
  keysFor(peerId) {
    return this._read()[String(peerId)] || [];
  }

  /**
   * Trust state for a peer key, WITHOUT recording anything.
   *  'pinned'     — exactly this key was seen before
   *  'tofu-new'   — first time we ever see this peer
   *  'new-device' — known peer, unknown key (could be a real new device, or an attack)
   */
  check(peerId, identityPubB64) {
    const known = this.keysFor(peerId);
    if (known.some(k => k.key === identityPubB64)) return 'pinned';
    return known.length === 0 ? 'tofu-new' : 'new-device';
  }

  /** Record a key for a peer (idempotent). Returns the state before recording. */
  pin(peerId, identityPubB64, label) {
    const state = this.check(peerId, identityPubB64);
    if (state === 'pinned') return state;
    const data = this._read();
    const id = String(peerId);
    data[id] = data[id] || [];
    data[id].push({
      key: identityPubB64,
      firstSeen: new Date().toISOString(),
      verified: false,
      ...(label ? { label } : {}),
    });
    this._write(data);
    return state;
  }

  /** Mark a key as verified out-of-band (SAS matched). */
  markVerified(peerId, identityPubB64) {
    const data = this._read();
    const entries = data[String(peerId)] || [];
    const hit = entries.find(k => k.key === identityPubB64);
    if (!hit) return false;
    hit.verified = true;
    hit.verifiedAt = new Date().toISOString();
    this._write(data);
    return true;
  }

  isVerified(peerId, identityPubB64) {
    return !!this.keysFor(peerId).find(k => k.key === identityPubB64 && k.verified);
  }

  /** Drop trust for a peer (all devices) — the next call starts at TOFU again. */
  forget(peerId) {
    const data = this._read();
    delete data[String(peerId)];
    this._write(data);
  }

  forgetAll() {
    this.storage.removeItem(this._key);
  }
}

/**
 * Full peer-authentication decision for one handshake.
 * Returns { state, ok, identityB64 } where state is one of:
 *   'invalid'    — signature does not verify → the call must NOT proceed
 *   'anonymous'  — peer sent no identity (guest, or a v1 client)
 *   'pinned' | 'tofu-new' | 'new-device' — see TofuStore.check
 */
export function authenticatePeer({ room, ephemeralPub, identityPub, sig, peerId, tofu }) {
  if (!identityPub) return { state: 'anonymous', ok: true, identityB64: null };
  if (!verifySignature(room, ephemeralPub, identityPub, sig)) {
    return { state: 'invalid', ok: false, identityB64: null };
  }
  const identityB64 = b64enc(identityPub);
  // First contact records the key — the TOFU bet. A NEW key for a KNOWN
  // peer records nothing: it used to be pinned on sight, so the second call
  // from the same substituted key came back 'pinned' and the warning was a
  // one-time event. Recording now happens only when the person confirms
  // (confirmPeerIdentity), which is what makes held_untrusted (SEC-05) a
  // gate rather than a formality.
  let state = 'tofu-new';
  if (tofu) {
    state = tofu.check(peerId, identityB64);
    if (state === 'tofu-new') tofu.pin(peerId, identityB64);
  }
  const verified = tofu ? tofu.isVerified(peerId, identityB64) : false;
  return { state, ok: true, identityB64, verified };
}
